import type { KeyboardEvent, RefObject } from 'react'
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  FileButton,
  Group,
  Image,
  Menu,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import {
  IconArrowLeft,
  IconArrowRight,
  IconBook,
  IconCalculator,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCornerDownRight,
  IconFile,
  IconHelp,
  IconInbox,
  IconInfoCircle,
  IconMessage,
  IconNotes,
  IconPackage,
  IconPaperclip,
  IconSend,
  IconSparkles,
  IconStar,
  IconX,
} from '@/components/icons'
import { ReplyEmojiPickerButton } from '@/components/tickets/ReplyEmojiPickerButton'
import { ReplyInstallerPickerButton } from '@/components/tickets/ReplyInstallerPickerButton'
import {
  THREAD_MESSAGE_FONT_SIZE,
  formatAttachmentFileSize,
  quotePreviewAuthorLabel,
} from '@/components/tickets/TicketMessageThread'
import type {
  InstallerRow,
  MacroRow,
  ProjectTemplateRow,
  TicketDetailMessage,
  TicketDetailTicket,
} from '@/types/api'

type ReplyTarget = 'customer' | 'internal'
type SendReplyOption = 'none' | 'next' | 'list' | 'next-resolve'

type WidgetEmailWaitState = {
  active: boolean
  replyTo: string
}

type TicketReplyComposerProps = {
  ticket: TicketDetailTicket
  collapsed: boolean
  minHeight: number
  isPhoneLayout: boolean
  isTicketWideLayout: boolean
  replyTarget: ReplyTarget
  replyText: string
  quotedMessage: TicketDetailMessage | null
  pinnedTemplates: ProjectTemplateRow[]
  pinnedMacros: MacroRow[]
  templates: ProjectTemplateRow[]
  macros: MacroRow[]
  files: File[]
  pendingInstallers: InstallerRow[]
  pendingInstallerIds: Set<string>
  replyImageBlobUrls: string[]
  refundCalculatorEnabled: boolean
  showCreateInstructionBtn: boolean
  sending: boolean
  resolving: boolean
  requireResolutionComment: boolean
  resolveConfirmationEnabled: boolean
  widgetEmailWait: WidgetEmailWaitState
  canViewKnowledgeBase?: boolean
  kbPanelOpen?: boolean
  similarPanelOpen?: boolean
  replyAreaRef: RefObject<HTMLTextAreaElement | null>
  replyFileIconResetRef: RefObject<(() => void) | null>
  onSetCollapsed: (collapsed: boolean) => void
  onChangeReplyTarget: (target: ReplyTarget) => void
  onOpenHelp: () => void
  onOpenAiInstruction: () => void
  onApplyTemplate: (template: ProjectTemplateRow) => void
  onInsertText: (text: string, options?: { withTrailingNewline?: boolean }) => void
  onClearQuote: () => void
  onChangeReplyText: (text: string) => void
  onProcessSlash: (text: string, cursorPosition: number) => void
  onReplyKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  onPickInstaller: (installer: InstallerRow) => void
  onOpenRefundCalculator: () => void
  onToggleKnowledgeBase?: () => void
  onToggleSimilarClosedTickets?: () => void
  onAppendFiles: (files: File[] | null) => void
  onRemovePendingInstallerAt: (index: number) => void
  onRemoveReplyFileAt: (index: number) => void
  onSummary: () => void
  onOpenVariables: () => void
  onOpenFavorites: () => void
  onPending: () => void
  onNextOpenTicket: () => void
  onSendReply: () => void
  onSendReplyToWidgetMailbox: () => void
  onSendReplyWithOptions: (option: SendReplyOption) => void
  onOpenResolveWithComment: (comment: string) => void
  onResolveTicketAndGoToList: () => void
  onCloseTicket: () => void
}

export function TicketReplyComposer({
  ticket,
  collapsed,
  minHeight,
  isPhoneLayout,
  isTicketWideLayout,
  replyTarget,
  replyText,
  quotedMessage,
  pinnedTemplates,
  pinnedMacros,
  templates,
  macros,
  files,
  pendingInstallers,
  pendingInstallerIds,
  replyImageBlobUrls,
  refundCalculatorEnabled,
  showCreateInstructionBtn,
  sending,
  resolving,
  requireResolutionComment,
  resolveConfirmationEnabled,
  widgetEmailWait,
  canViewKnowledgeBase,
  kbPanelOpen,
  similarPanelOpen,
  replyAreaRef,
  replyFileIconResetRef,
  onSetCollapsed,
  onChangeReplyTarget,
  onOpenHelp,
  onOpenAiInstruction,
  onApplyTemplate,
  onInsertText,
  onClearQuote,
  onChangeReplyText,
  onProcessSlash,
  onReplyKeyDown,
  onPickInstaller,
  onOpenRefundCalculator,
  onToggleKnowledgeBase,
  onToggleSimilarClosedTickets,
  onAppendFiles,
  onRemovePendingInstallerAt,
  onRemoveReplyFileAt,
  onSummary,
  onOpenVariables,
  onOpenFavorites,
  onPending,
  onNextOpenTicket,
  onSendReply,
  onSendReplyToWidgetMailbox,
  onSendReplyWithOptions,
  onOpenResolveWithComment,
  onResolveTicketAndGoToList,
  onCloseTicket,
}: TicketReplyComposerProps) {
  if (ticket.status === 'closed') {
    return (
      <Card withBorder padding="sm" radius="md" style={{ flexShrink: 0, minWidth: 0 }}>
        <Text fw={600} size="xs" mb={6}>
          Ответ
        </Text>
        <Alert color="gray" variant="light" p="xs">
          <Text size="xs">Обращение закрыто, новые сообщения недоступны.</Text>
        </Alert>
      </Card>
    )
  }

  if (collapsed) {
    return (
      <Card withBorder padding="xs" radius="md" style={{ flexShrink: 0, flexGrow: 0, minWidth: 0 }}>
        <UnstyledButton type="button" style={{ width: '100%' }} onClick={() => onSetCollapsed(false)}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="xs" fw={600}>
              Ответ — развернуть
            </Text>
            <IconChevronUp size={16} stroke={1.5} />
          </Group>
        </UnstyledButton>
      </Card>
    )
  }

  const requestSummary = () => {
    openConfirmModal({
      title: 'Запросить саммари?',
      children: (
        <Text size="sm" c="dimmed">
          Будет отправлен запрос на генерацию краткого содержания переписки.
        </Text>
      ),
      labels: { confirm: 'Запросить', cancel: 'Отмена' },
      onConfirm: onSummary,
    })
  }

  const requestPending = () => {
    onPending()
  }

  const requestResolve = () => {
    if (requireResolutionComment) {
      if (!resolveConfirmationEnabled) {
        onOpenResolveWithComment(replyText.trim())
        return
      }
      openConfirmModal({
        title: 'Решить обращение?',
        children: (
          <Text size="sm" c="dimmed">
            Откроется форма с комментарием к решению. После подтверждения черновик ответа
            (если есть) уйдёт клиенту, статус станет «Решено», вы вернётесь к списку.
          </Text>
        ),
        labels: { confirm: 'Продолжить', cancel: 'Отмена' },
        confirmProps: { color: 'green' },
        onConfirm: () => onOpenResolveWithComment(replyText.trim()),
      })
      return
    }
    if (!resolveConfirmationEnabled) {
      onResolveTicketAndGoToList()
      return
    }
    openConfirmModal({
      title: 'Решить обращение?',
      children: (
        <Text size="sm" c="dimmed">
          Если в поле ответа есть текст или файлы, они будут отправлены клиенту. Статус
          станет «Решено», вкладка карточки закроется и откроется список обращений.
        </Text>
      ),
      labels: { confirm: 'Решить', cancel: 'Отмена' },
      confirmProps: { color: 'green' },
      onConfirm: onResolveTicketAndGoToList,
    })
  }

  const requestCloseTicket = () => {
    openConfirmModal({
      title: 'Закрыть обращение?',
      children: (
        <Text size="sm" c="dimmed">
          Закрытое обращение нельзя будет дополнять сообщениями от имени клиента.
        </Text>
      ),
      labels: { confirm: 'Закрыть', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: onCloseTicket,
    })
  }

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{
        flexShrink: 0,
        flexGrow: 0,
        minWidth: 0,
        height: 'auto',
        maxHeight: 'none',
        minHeight,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group justify="space-between" wrap="nowrap" mb={6} style={{ flexShrink: 0 }}>
        <Text fw={600} size="xs">
          Ответ
        </Text>
        <Tooltip label="Свернуть" withArrow>
          <ActionIcon
            size="sm"
            variant="subtle"
            aria-label="Свернуть блок ответа"
            onClick={() => onSetCollapsed(true)}
          >
            <IconChevronDown size={16} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Stack gap="xs" style={{ flex: '0 0 auto', overflow: 'visible' }}>
        {ticket.status === 'resolved' ? (
          <Alert color="yellow" variant="light" p="xs" style={{ flexShrink: 0 }}>
            <Text size="xs">Решено: ответ снова откроет в работу.</Text>
          </Alert>
        ) : null}
        <Box
          p={4}
          style={{
            borderRadius: 6,
            background: 'var(--mantine-color-default-hover)',
            flexShrink: 0,
          }}
        >
          <Group gap={6} wrap="wrap" align="flex-start" justify="flex-start">
            <Button
              size="xs"
              color={replyTarget === 'customer' ? undefined : 'gray'}
              variant={replyTarget === 'customer' ? 'filled' : 'subtle'}
              leftSection={<IconMessage size={14} stroke={1.5} />}
              onClick={() => onChangeReplyTarget('customer')}
              style={{ flex: isPhoneLayout ? '1 1 calc(50% - 4px)' : undefined }}
            >
              {isPhoneLayout ? 'Клиенту' : 'Ответ пользователю'}
            </Button>
            <Button
              size="xs"
              color="orange"
              variant={replyTarget === 'internal' ? 'filled' : 'subtle'}
              leftSection={<IconNotes size={14} stroke={1.5} />}
              onClick={() => onChangeReplyTarget('internal')}
              style={{ flex: isPhoneLayout ? '1 1 calc(50% - 4px)' : undefined }}
            >
              {isPhoneLayout ? 'Заметка' : 'Внутренняя заметка'}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconHelp size={14} stroke={1.5} />}
              onClick={onOpenHelp}
            >
              {isPhoneLayout ? 'Помощь' : 'Помощь коллег'}
            </Button>
            {showCreateInstructionBtn && ticket.status !== 'closed' ? (
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconSparkles size={14} stroke={1.5} />}
                title="Создать инструкцию по диалогу"
                onClick={onOpenAiInstruction}
              >
                {isPhoneLayout ? 'Инструкция' : 'Создать инструкцию'}
              </Button>
            ) : null}
          </Group>
        </Box>
        {pinnedTemplates.length > 0 || pinnedMacros.length > 0 ? (
          <Group gap={4} wrap="wrap" style={{ flexShrink: 0 }}>
            {pinnedTemplates.map((tpl) => (
              <Button key={tpl.id} size="xs" variant="light" onClick={() => onApplyTemplate(tpl)}>
                {tpl.name}
              </Button>
            ))}
            {pinnedMacros.map((m) => (
              <Button
                key={m.id}
                size="xs"
                variant="outline"
                onClick={() => onInsertText(m.content || '', { withTrailingNewline: true })}
              >
                {m.name}
              </Button>
            ))}
          </Group>
        ) : null}
        {quotedMessage ? (
          <Box
            p="xs"
            style={{
              flexShrink: 0,
              borderRadius: 6,
              background: 'light-dark(rgba(0,0,0,0.04), rgba(255,255,255,0.06))',
              borderLeft: '3px solid var(--mantine-color-blue-6)',
            }}
          >
            <Group justify="space-between" wrap="nowrap" gap={6} mb={6}>
              <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                <IconCornerDownRight
                  size={14}
                  stroke={1.5}
                  style={{ flexShrink: 0, color: 'var(--mantine-color-blue-6)' }}
                />
                <Text size="xs" fw={600} lineClamp={1} style={{ minWidth: 0 }}>
                  {quotePreviewAuthorLabel(quotedMessage)}
                </Text>
              </Group>
              <Tooltip label="Убрать цитату" withArrow>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  aria-label="Убрать цитату"
                  onClick={onClearQuote}
                >
                  <IconX size={14} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Tooltip
              label={quotedMessage.content || ''}
              multiline
              maw={420}
              openDelay={400}
              disabled={!(quotedMessage.content || '').trim()}
            >
              <Text
                size="xs"
                c="dimmed"
                lineClamp={3}
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: THREAD_MESSAGE_FONT_SIZE,
                  lineHeight: 1.45,
                }}
              >
                {quotedMessage.content || ''}
              </Text>
            </Tooltip>
          </Box>
        ) : null}
        <Box
          pos="relative"
          style={{
            flex: '0 0 auto',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Textarea
            ref={replyAreaRef}
            size="xs"
            autosize
            minRows={4}
            placeholder={
              replyTarget === 'internal'
                ? 'Введите внутреннюю заметку… Наберите / — шаблоны и макросы.'
                : 'Ответ… Наберите / — шаблоны и макросы; [имя] — переменные (см. ⓘ)'
            }
            value={replyText}
            onChange={(e) => {
              const v = e.currentTarget.value
              const pos = e.currentTarget.selectionStart ?? v.length
              onChangeReplyText(v)
              onProcessSlash(v, pos)
            }}
            onKeyDown={onReplyKeyDown}
            onClick={(e) => {
              const ta = e.currentTarget
              onProcessSlash(ta.value, ta.selectionStart ?? ta.value.length)
            }}
            styles={{
              root: {
                flex: '0 0 auto',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              },
              wrapper: {
                display: 'flex',
                alignItems: 'stretch',
              },
              input: {
                flex: 'none',
                alignSelf: 'stretch',
                width: '100%',
                resize: 'none',
                fontSize: THREAD_MESSAGE_FONT_SIZE,
                lineHeight: 1.45,
                paddingBottom: 36,
                paddingRight:
                  canViewKnowledgeBase || ticket.project_id
                    ? isTicketWideLayout
                      ? 180
                      : isPhoneLayout
                        ? 148
                        : 168
                    : isTicketWideLayout
                      ? 100
                      : isPhoneLayout
                        ? 72
                        : 88,
                ...(replyTarget === 'internal'
                  ? {
                      borderColor: '#f59e0b',
                      borderWidth: 2,
                    }
                  : {}),
              },
            }}
          />
          <Group
            gap={4}
            pos="absolute"
            bottom={8}
            right={8}
            wrap="nowrap"
            style={{ pointerEvents: 'none' }}
          >
            {canViewKnowledgeBase ? (
              <Tooltip label="Открыть базу знаний" withArrow>
                <ActionIcon
                  variant={kbPanelOpen ? 'light' : 'subtle'}
                  color={kbPanelOpen ? 'brand' : 'gray'}
                  size="sm"
                  radius="md"
                  aria-label="База знаний"
                  onClick={onToggleKnowledgeBase}
                  style={{ pointerEvents: 'auto' }}
                >
                  <IconBook size={16} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            ) : null}
            {ticket.project_id ? (
              <Tooltip label="Похожие обращения" withArrow>
                <ActionIcon
                  variant={similarPanelOpen ? 'light' : 'subtle'}
                  color={similarPanelOpen ? 'brand' : 'gray'}
                  size="sm"
                  radius="md"
                  aria-label="Похожие обращения"
                  onClick={onToggleSimilarClosedTickets}
                  style={{ pointerEvents: 'auto' }}
                >
                  <IconMessage size={16} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            ) : null}
            <ReplyInstallerPickerButton existingIds={pendingInstallerIds} onPick={onPickInstaller} />
            {refundCalculatorEnabled ? (
              <Tooltip label="Калькулятор возврата" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  aria-label="Калькулятор возврата"
                  onClick={onOpenRefundCalculator}
                  style={{ pointerEvents: 'auto' }}
                >
                  <IconCalculator size={16} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            ) : null}
            <FileButton resetRef={replyFileIconResetRef} onChange={onAppendFiles} accept="*/*" multiple>
              {(props) => (
                <ActionIcon
                  {...props}
                  variant="subtle"
                  size="sm"
                  aria-label="Добавить файлы"
                  style={{ pointerEvents: 'auto' }}
                >
                  <IconPaperclip size={16} stroke={1.5} />
                </ActionIcon>
              )}
            </FileButton>
            <ReplyEmojiPickerButton onInsert={(emoji) => onInsertText(emoji)} />
          </Group>
        </Box>
        {files.length > 0 || pendingInstallers.length > 0 ? (
          <Stack gap={8} style={{ flexShrink: 0, overflow: 'visible' }}>
            {pendingInstallers.map((inst, pi) => (
              <Group
                key={inst.id}
                wrap="nowrap"
                gap={8}
                p={8}
                style={{
                  borderRadius: 6,
                  border: '1px solid var(--mantine-color-default-border)',
                  background: 'var(--mantine-color-body)',
                }}
              >
                <IconPackage size={18} stroke={1.5} style={{ flexShrink: 0, opacity: 0.7 }} />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="xs" fw={500} lineClamp={1}>
                    {inst.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatAttachmentFileSize(inst.file_size)} · установщик
                  </Text>
                </Box>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  aria-label="Убрать установщик"
                  onClick={() => onRemovePendingInstallerAt(pi)}
                >
                  <IconX size={14} stroke={1.5} />
                </ActionIcon>
              </Group>
            ))}
            {replyImageBlobUrls.some(Boolean) ? (
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                  gap: 8,
                }}
              >
                {files.map((f, i) => {
                  const blob = replyImageBlobUrls[i]
                  if (!blob) return null
                  const key = `${f.name}-${f.size}-${f.lastModified}`
                  return (
                    <Box
                      key={key}
                      pos="relative"
                      style={{
                        aspectRatio: '1',
                        borderRadius: 6,
                        overflow: 'hidden',
                        border: '1px solid var(--mantine-color-default-border)',
                      }}
                    >
                      <Image src={blob} alt={f.name} fit="cover" h="100%" w="100%" />
                      <ActionIcon
                        size="sm"
                        color="red"
                        variant="filled"
                        radius="xl"
                        aria-label="Убрать файл"
                        style={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}
                        onClick={() => onRemoveReplyFileAt(i)}
                      >
                        <IconX size={12} stroke={1.5} />
                      </ActionIcon>
                    </Box>
                  )
                })}
              </Box>
            ) : null}
            {files.map((f, i) => {
              if (replyImageBlobUrls[i]) return null
              const key = `${f.name}-${f.size}-${f.lastModified}`
              return (
                <Group
                  key={key}
                  wrap="nowrap"
                  gap={8}
                  p={8}
                  style={{
                    borderRadius: 6,
                    border: '1px solid var(--mantine-color-default-border)',
                    background: 'var(--mantine-color-body)',
                  }}
                >
                  <IconFile size={18} stroke={1.5} style={{ flexShrink: 0, opacity: 0.7 }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" fw={500} lineClamp={1}>
                      {f.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatAttachmentFileSize(f.size)}
                    </Text>
                  </Box>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    aria-label="Убрать файл"
                    onClick={() => onRemoveReplyFileAt(i)}
                  >
                    <IconX size={14} stroke={1.5} />
                  </ActionIcon>
                </Group>
              )
            })}
          </Stack>
        ) : null}
        {replyTarget === 'internal' ? (
          <Button
            variant="light"
            size="compact-xs"
            leftSection={<IconStar size={12} stroke={1.5} />}
            onClick={requestSummary}
            style={{ flexShrink: 0, alignSelf: 'flex-start' }}
          >
            Запросить саммари диалога
          </Button>
        ) : null}
        <Group
          justify="space-between"
          align="flex-end"
          gap="xs"
          wrap="wrap"
          style={{ flexShrink: 0, width: '100%' }}
        >
          <Group gap={6} wrap="wrap" align="center" style={{ flex: '1 1 200px', minWidth: 0 }}>
            {templates.length > 0 ? (
              <Menu shadow="md" width={280} position="top-start" returnFocus={false}>
                <Menu.Target>
                  <Button
                    size="compact-xs"
                    variant="default"
                    rightSection={<IconChevronDown size={14} stroke={1.5} />}
                  >
                    Шаблон…
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {templates.map((tpl) => (
                    <Menu.Item key={tpl.id} onClick={() => onApplyTemplate(tpl)}>
                      {tpl.name}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            ) : null}
            <Menu shadow="md" width={300} position="top-start" returnFocus={false}>
              <Menu.Target>
                <Button
                  size="compact-xs"
                  variant="default"
                  rightSection={<IconChevronDown size={14} stroke={1.5} />}
                >
                  Макросы
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {macros.length === 0 ? (
                  <Menu.Item disabled>Нет макросов</Menu.Item>
                ) : (
                  macros.map((m) => (
                    <Menu.Item
                      key={m.id}
                      onClick={() => onInsertText(m.content || '', { withTrailingNewline: true })}
                    >
                      {m.name}
                    </Menu.Item>
                  ))
                )}
              </Menu.Dropdown>
            </Menu>
            <Tooltip label="Переменные" withArrow>
              <ActionIcon variant="default" size="sm" aria-label="Переменные" onClick={onOpenVariables}>
                <IconInfoCircle size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Частые шаблоны и макросы" withArrow>
              <ActionIcon variant="subtle" size="sm" aria-label="Избранное" onClick={onOpenFavorites}>
                <IconStar size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Group gap={6} wrap="wrap" justify="flex-end" style={{ flex: '1 1 100%', minWidth: 0 }}>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconClock size={14} stroke={1.5} />}
              onClick={requestPending}
            >
              В ожидание
            </Button>
            <Tooltip label="Следующее неназначенное (open / in progress)" withArrow>
              <Button
                variant="default"
                size="xs"
                leftSection={<IconArrowRight size={14} stroke={1.5} />}
                onClick={onNextOpenTicket}
              >
                К следующему
              </Button>
            </Tooltip>
            <Group gap={0} wrap="nowrap">
              <Button
                variant="filled"
                color={replyTarget === 'internal' ? 'orange' : undefined}
                size="xs"
                leftSection={<IconSend size={14} stroke={1.5} />}
                loading={sending}
                onClick={onSendReply}
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              >
                Отправить
              </Button>
              {String(ticket.channel_type || '').toLowerCase() === 'widget' &&
              widgetEmailWait.active &&
              replyTarget === 'customer' ? (
                <Button
                  variant="light"
                  color="blue"
                  size="xs"
                  leftSection={<IconInbox size={14} stroke={1.5} />}
                  loading={sending}
                  onClick={onSendReplyToWidgetMailbox}
                  style={{ borderRadius: 0 }}
                >
                  Ответить на почту
                </Button>
              ) : null}
              <Menu shadow="md" width={300} position="top-end" withinPortal>
                <Menu.Target>
                  <Button
                    variant="filled"
                    color={replyTarget === 'internal' ? 'orange' : undefined}
                    size="xs"
                    px={6}
                    aria-label="Дополнительные варианты отправки"
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    disabled={sending}
                  >
                    <IconChevronDown size={16} stroke={1.5} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconArrowRight size={14} stroke={1.5} />}
                    onClick={() => onSendReplyWithOptions('next')}
                  >
                    Ответить и перейти к следующему
                  </Menu.Item>
                  {ticket.status !== 'resolved' && ticket.status !== 'closed' ? (
                    <Menu.Item
                      leftSection={<IconCheck size={14} stroke={1.5} />}
                      onClick={() => onSendReplyWithOptions('next-resolve')}
                    >
                      Ответить, решить и перейти к следующему
                    </Menu.Item>
                  ) : null}
                  <Menu.Item
                    leftSection={<IconArrowLeft size={14} stroke={1.5} />}
                    onClick={() => onSendReplyWithOptions('list')}
                  >
                    Ответить и вернуться к списку
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
            {ticket.status !== 'resolved' && ticket.status !== 'closed' ? (
              <Tooltip label="Ctrl+Alt+S — отправить в клиента и решить, остаться здесь" withArrow>
                <Button
                  color="green"
                  size="xs"
                  leftSection={<IconCheck size={14} stroke={1.5} />}
                  loading={resolving}
                  onClick={requestResolve}
                >
                  Решить
                </Button>
              </Tooltip>
            ) : null}
            {ticket.status === 'resolved' ? (
              <Button variant="outline" size="xs" onClick={requestCloseTicket}>
                Закрыть
              </Button>
            ) : null}
          </Group>
        </Group>
      </Stack>
    </Card>
  )
}
